// The default reading of a tool call NO lens claimed.
//
// The per-trace X-ray's honest score is its residual: every tool_use no
// extractor recognized stays in the feed rather than being dropped, so the
// reading can't silently overstate what it classified. But visible was not the
// same as recoverable — a residual row named the tool and showed nothing at
// all, which made the one stretch of a trace with no interpretation also the
// one stretch with no content. This reads the raw pair back out of the turns:
// the call as issued, the result as returned, and the block coordinates of
// both so the reader can land on either in the Context reading.
//
// Reads from `turns` on demand rather than riding along on the feed row. The
// component already holds the turns, and a single Bash result runs to tens of
// kilobytes — copying every unclaimed payload onto its op would grow the feed
// by the combined size of exactly the calls nobody could interpret.

import { mediaOfBlock, mediaSummary } from "./traceFileTree.js";
import { cellInnerReading, parseCellEnvelope, cellEnvelopeNote, isWaitBlock } from "./traceCodeModeCells.js";
import { formatConversationValue } from "./conversationContent.js";

// Caps are display bounds, not truncation of the record: the Context and Raw
// readings in the same panel still hold the whole payload, and the source chips
// point at them. A cap that hides that it capped would be the actual problem,
// so both report whether they clipped.
const CALL_CAP = 1600;
const RESULT_CAP = 4000;
const TURN_BLOCK_CAP = 2400;
// How far past a call the collapsed-row read looks for its result. The harness
// returns one in the next turn; anything beyond this is left unknown rather than
// paid for on every unclaimed call in the trace.
const HEAD_LOOKAHEAD_TURNS = 2;

function clipAt(text, cap) {
  const s = typeof text === "string" ? text : "";
  return s.length > cap ? { text: s.slice(0, cap), clipped: s.length - cap } : { text: s, clipped: 0 };
}

/**
 * A tool call's input as the reader's first look at it: one `key: value` per
 * line, scalars bare and structures as compact JSON. The full input stays
 * available as `callJson`.
 */
export function toolCallText(input) {
  if (input == null) return "";
  if (typeof input !== "object") return String(input);
  if (Array.isArray(input)) return JSON.stringify(input, null, 2);
  const entries = Object.entries(input);
  if (!entries.length) return "";
  // A lone argument needs no key to read as itself — `command` is the whole
  // story of a Bash call, and prefixing it only spends a line on the obvious.
  if (entries.length === 1) {
    const [, v] = entries[0];
    if (typeof v === "string") return v;
  }
  return entries
    .map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`)
    .join("\n");
}

function pointerToken(value) {
  return String(value).replaceAll("~", "~0").replaceAll("/", "~1");
}

function escapedStringBoundaries(value) {
  const boundaries = Array(value.length + 1).fill(null);
  let sourceOffset = 0;
  let renderedOffset = 0;
  boundaries[0] = 0;
  for (const character of value) {
    renderedOffset += JSON.stringify(character).slice(1, -1).length;
    sourceOffset += character.length;
    boundaries[sourceOffset] = renderedOffset;
  }
  return boundaries;
}

function offsetSegments(segments, offset) {
  return segments.map((segment) => ({
    ...segment,
    start: segment.start + offset,
    end: segment.end + offset,
  }));
}

function jsonValueProjection(value, fieldPath = "", level = 0) {
  if (typeof value === "string") {
    const text = JSON.stringify(value);
    return {
      text,
      segments: [{
        fieldPath,
        start: 1,
        end: text.length - 1,
        sourceBoundaries: escapedStringBoundaries(value),
      }],
    };
  }
  if (Array.isArray(value)) {
    if (!value.length) return { text: "[]", segments: [] };
    let text = "[\n";
    const segments = [];
    value.forEach((item, index) => {
      if (index) text += ",\n";
      text += "  ".repeat(level + 1);
      const child = jsonValueProjection(item, `${fieldPath}/${index}`, level + 1);
      segments.push(...offsetSegments(child.segments, text.length));
      text += child.text;
    });
    text += `\n${"  ".repeat(level)}]`;
    return { text, segments };
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value);
    if (!entries.length) return { text: "{}", segments: [] };
    let text = "{\n";
    const segments = [];
    entries.forEach(([key, item], index) => {
      if (index) text += ",\n";
      text += `${"  ".repeat(level + 1)}${JSON.stringify(key)}: `;
      const child = jsonValueProjection(
        item,
        `${fieldPath}/${pointerToken(key)}`,
        level + 1,
      );
      segments.push(...offsetSegments(child.segments, text.length));
      text += child.text;
    });
    text += `\n${"  ".repeat(level)}}`;
    return { text, segments };
  }
  return { text: JSON.stringify(value) ?? "", segments: [] };
}

/**
 * A tool_result's content (string | block[]) flattened for display. Mirrors the
 * Context reading's own handling so the same call reads the same either side of
 * the pivot.
 */
export function toolResultProjection(content) {
  if (typeof content === "string") {
    return { text: content, segments: [{ fieldPath: "", start: 0, end: content.length }] };
  }
  if (!Array.isArray(content)) {
    return content && typeof content === "object"
      ? jsonValueProjection(content)
      : { text: "", segments: [] };
  }
  const parts = [];
  const segments = [];
  let renderedLength = 0;
  content.forEach((b, index) => {
      const start = renderedLength + (parts.length ? 2 : 0);
      let text = "";
      let projectedSegments = [];
      if (typeof b === "string") {
        text = b;
        projectedSegments = [{ fieldPath: `/${index}`, start: 0, end: text.length }];
      } else if (!b || typeof b !== "object") {
        return;
      }
      if (b.type === "text" && typeof b.text === "string") {
        text = b.text;
        if (text) {
          projectedSegments = [{ fieldPath: `/${index}/text`, start: 0, end: text.length }];
        }
      }
      // An attachment result carries a descriptor: the media type, and how big
      // the payload was before the capture parser dropped it. A bare "[image]"
      // throws all of that away and reads as "nothing was captured here", which
      // is the opposite of what the block says - the agent saw an image of a
      // stated type and size, and only the bytes are absent. A block with no
      // descriptor at all still reads as the bare placeholder, because that is
      // genuinely all it says.
      if (!text && b.type !== "text") {
        const media = mediaOfBlock(b);
        if (media) {
          text = `[${mediaSummary(media) || b.type}]`;
        } else {
          const projection = jsonValueProjection(b, `/${index}`);
          text = projection.text;
          projectedSegments = projection.segments;
        }
      }
      if (text) {
        segments.push(...offsetSegments(projectedSegments, start));
        parts.push(text);
        renderedLength = start + text.length;
      }
    });
  return { text: parts.join("\n\n"), segments };
}

export function toolResultText(content) {
  return toolResultProjection(content).text;
}

/**
 * Just enough of a call to put on its collapsed row: the tool's name and the
 * first line of what it actually did.
 *
 * Separate from readToolCall because this runs for EVERY unclaimed call on
 * mount and must not pay for the result scan. Without it a residual run reads as
 * a stack of identical "Bash" rows that have to be opened one at a time to find
 * the one you want — the tool name is the least informative thing about a shell
 * call.
 */
export function readToolCallHead(turns, turnIndex, blockIndex) {
  const block = turns?.[turnIndex]?.blocks?.[blockIndex];
  if (!block || block.type !== "tool_use") return null;
  // A code-mode cell's first line is a `const r = await tools.…` preamble; the
  // inner command is what the call actually did, so it leads instead.
  const cell = cellInnerReading(block, turns[turnIndex].blocks);
  const lines = (cell ? cell.text : toolCallText(block.input)).split("\n").filter((l) => l.trim());
  // Whether it failed belongs on the COLLAPSED row — that is the one fact that
  // decides whether a reader opens this call at all. Found through a bounded
  // lookahead rather than the full scan readToolCall does: the harness returns a
  // result in the turn immediately after the call, so a short window catches
  // every real pairing without making a trace full of unpaired calls quadratic.
  // Outside the window the answer is unknown, which is reported as null — not as
  // "did not fail".
  // The host envelope wraps every cell-adjacent result — the cell's own, and
  // a wait poll's — so it is parsed wherever it appears, not only on cells.
  const paired = findResult(turns, block.id, turnIndex, turnIndex + HEAD_LOOKAHEAD_TURNS);
  const env = paired ? parseCellEnvelope(paired.block.content) : null;
  return {
    name: cell ? `${block.name} · ${cell.tools.join(" · ")}` : String(block.name || "tool"),
    firstLine: lines[0] || "",
    moreLines: Math.max(0, lines.length - 1),
    failed: paired ? Boolean(paired.block.isError) || (env?.exitCode != null && env.exitCode !== 0) : null,
  };
}

/**
 * One whole turn, read back for inline preview.
 *
 * This is what makes the X-ray reading self-contained: an evidence chip resolves
 * to the turn itself, right here, instead of navigating to the Context reading
 * and losing the reader's place in the feed. Per-block caps keep a turn carrying
 * a 40k tool result from becoming the page; a caller with one deliberately
 * opened turn (the Lanes dock's show-full toggle) may pass `cap: Infinity`.
 *
 * `results` reads each call's result under the call. The harness returns a
 * result in a later turn, so a call's own turn reads, alone, like a call with
 * no answer. Those rows carry `result: true` and the turn they arrived in; they
 * are not blocks of this turn, so `own` still counts the turn's own blocks.
 */
export function readTurn(turns, turnIndex, cap = TURN_BLOCK_CAP, { results = false } = {}) {
  const turn = turns?.[turnIndex];
  if (!turn) return null;
  const read = (text) => clipAt(text, cap);
  // Virtual blocks are the unwrap's DERIVATIONS of a cell, not blocks the
  // turn held — the cell row below already reads as its inner command, so
  // repeating each derivation would show the turn holding more than it did.
  const own = (turn.blocks || []).filter((b) => !b?.virtual);
  const rows = own
    .map((b) => {
      if (b?.type === "text") return { label: "Text", ...read(b.text) };
      if (b?.type === "thinking") return { label: "Thinking", ...read(b.thinking || b.text) };
      if (b?.type === "tool_use") return callRow(b, turn.blocks, read);
      if (b?.type === "tool_result") {
        const env = parseCellEnvelope(b.content);
        const note = cellEnvelopeNote(env);
        return {
          label: `${b.linkedToolName || "Tool"} result${note ? ` · ${note}` : ""}`,
          failed: Boolean(b.isError) || (env?.exitCode != null && env.exitCode !== 0),
          ...read(env ? env.text : toolResultText(b.content)),
        };
      }
      return { label: b?.type || "Block", text: "", clipped: 0 };
    });
  const blocks = results
    ? rows.flatMap((row, i) => (own[i]?.type === "tool_use" ? [row, ...resultRowsOf(turns, turnIndex, own[i], read)] : [row]))
    : rows;
  // A block that carries no readable text still counts as a block — dropping it
  // would make the preview claim the turn held less than it did.
  return { role: String(turn.role || "?"), blocks, own: own.length };
}

/**
 * One turn as the record holds it, part by part: nothing capped, reformatted,
 * stripped or brought in from another turn. Text and thinking read verbatim;
 * a call's input, and anything else structured, read as JSON. Each part names
 * its type and its recorded facts, and a call or result the turn holding its
 * other half (`pair`), where the trace has it. Null for a turn not held.
 */
export function rawTurn(turns, turnIndex) {
  const turn = turns?.[turnIndex];
  if (!turn) return null;
  // Virtual blocks are the code-mode unwrap's derivations, not recorded parts.
  const own = (turn.blocks || []).filter((b) => !b?.virtual);
  return {
    role: String(turn.role || "?"),
    messageId: turn.messageId || null,
    oversized: Boolean(turn.oversized),
    parts: turn.oversized ? [] : own.map((b) => rawPart(turns, turnIndex, b)),
  };
}

const asJson = (value) => JSON.stringify(value ?? null, null, 2) ?? "";

// A call's input as the record typed it, which keeps what the plain copy
// cannot (an integer past what a number holds, a float's own spelling); null
// when there is none, or it does not read.
function typedJson(value) {
  if (!value) return null;
  try {
    return formatConversationValue(value);
  } catch {
    return null;
  }
}

// What a block carries besides its type, its body and the facts named on its
// head, and besides what the reading derives onto it (`facets`, a result's
// linked name, the typed copies of a call's input and a result's content).
const RAW_HEAD_KEYS = new Set([
  "type", "text", "thinking", "input", "content", "id", "name", "tool_use_id", "tool_name", "is_error", "isError",
  "elided", "bodyBytes", "facets", "linkedToolName", "typedInput", "typedContent", "virtual", "codeMode",
]);

function rawPart(turns, turnIndex, b) {
  const type = typeof b?.type === "string" && b.type ? b.type : "unknown";
  if (b?.elided) {
    // The record served this part without its body: the text the turn holds
    // here is the reader's own placeholder, not the part.
    return { type, facts: [`not served: ${formatBytes(b.bodyBytes)}, over the record's limit for one part`], body: null, items: [], pair: null, failed: false };
  }
  const facts = [];
  const items = [];
  let body = null;
  let pair = null;
  if (type === "text") body = String(b.text ?? "");
  else if (type === "thinking") body = String(b.thinking ?? b.text ?? "");
  else if (type === "tool_use") {
    if (b.name) facts.push(String(b.name));
    if (b.id) facts.push(`id ${b.id}`);
    body = typedJson(b.typedInput) ?? asJson(b.input);
    const res = findResult(turns, b.id, turnIndex);
    if (res && res.turnIndex !== turnIndex) pair = { turn: res.turnIndex, label: "result" };
  } else if (type === "tool_result") {
    if (b.tool_use_id) facts.push(`for ${b.tool_use_id}`);
    if (b.tool_name) facts.push(String(b.tool_name));
    if (b.is_error ?? b.isError) facts.push("error");
    const content = b.content;
    if (typeof content === "string") body = content;
    else if (Array.isArray(content)) {
      // Each item as itself: words verbatim, anything else as JSON.
      content.forEach((item, i) => {
        const text = typeof item === "string" ? item : item?.type === "text" && typeof item.text === "string" ? item.text : null;
        items.push({ label: `content ${i + 1} \u00b7 ${text != null ? "text" : item?.type || typeof item}`, body: text ?? asJson(item) });
      });
    } else if (content != null) body = asJson(content);
    const call = findCall(turns, b.tool_use_id, turnIndex);
    if (call != null && call !== turnIndex) pair = { turn: call, label: "call" };
  } else body = asJson(Object.fromEntries(Object.entries(b || {}).filter(([k]) => k !== "type")));
  const size = body != null ? body.length : items.reduce((n, it) => n + it.body.length, 0);
  facts.push(`${size.toLocaleString("en-US")} character${size === 1 ? "" : "s"}`);
  if (type === "text" || type === "thinking" || type === "tool_use" || type === "tool_result") {
    const rest = Object.fromEntries(Object.entries(b || {}).filter(([k, v]) => !RAW_HEAD_KEYS.has(k) && v !== undefined));
    if (Object.keys(rest).length) items.push({ label: "other fields", body: asJson(rest) });
  }
  return { type, facts, body, items, pair, failed: type === "tool_result" && Boolean(b.is_error ?? b.isError) };
}

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} KB`;
}

// The turn holding the call a result answers: the nearest one at or before
// the result's own, so a positional id used again later is not taken for it.
function findCall(turns, id, fromTurn) {
  if (!id) return null;
  for (let ti = Math.min(fromTurn, turns.length - 1); ti >= 0; ti--) {
    if ((turns[ti]?.blocks || []).some((b) => b?.type === "tool_use" && b.id === id && !b.virtual)) return ti;
  }
  return null;
}

// A call as the reading shows it: a code-mode cell reads as its inner commands.
function callRow(block, turnBlocks, read) {
  const cell = cellInnerReading(block, turnBlocks);
  if (cell) return { label: `${block.name} · ${cell.tools.join(" · ")}`, ...read(cell.text) };
  return { label: block.name || "Tool", ...read(toolCallText(block.input)) };
}

/**
 * The call behind one op and what came back for it, for a reader asking why
 * the op's subject shows what it does. An op the code-mode unwrap derived
 * points at a virtual inner call whose result is the unwrap's own parse; what
 * came back raw is its cell's result, and its wait polls, so that is what is
 * read. `{ label, text, clipped, results }`, or null when the coordinates are
 * not a call.
 */
export function readCall(turns, turnIndex, blockIndex, cap = TURN_BLOCK_CAP) {
  const turn = turns?.[turnIndex];
  const block = turn?.blocks?.[blockIndex];
  if (!block || block.type !== "tool_use") return null;
  const read = (text) => clipAt(text, cap);
  const parent = block.virtual && block.codeMode?.parent
    ? (turn.blocks || []).find((b) => !b?.virtual && b?.id === block.codeMode.parent)
    : null;
  return { ...callRow(block, turn.blocks, read), results: resultRowsOf(turns, turnIndex, parent || block, read) };
}

// What came back for one call, wherever it arrived. A code-mode cell that
// yielded ("running with cell ID N") finishes in later wait(N) polls, so its
// rows are its own result and then each poll's, in order, through the one
// that finished: the output as it arrived, not a single stitched answer.
function resultRowsOf(turns, fromTurn, call, read) {
  const res = findResult(turns, call.id, fromTurn);
  if (!res) return [{ label: "Result", result: true, missing: true, text: "", clipped: 0 }];
  const env = parseCellEnvelope(res.block.content);
  const rows = [resultRow(res, env, null, read)];
  if (env?.status === "running" && env.cellId) {
    for (const poll of pollsOf(turns, res.turnIndex, env.cellId)) rows.push(resultRow(poll.res, poll.env, "wait", read));
  }
  return rows;
}

function resultRow(res, env, via, read) {
  return {
    label: "Result",
    result: true,
    turnIndex: res.turnIndex,
    via,
    note: cellEnvelopeNote(env),
    failed: Boolean(res.block.isError) || (env?.exitCode != null && env.exitCode !== 0),
    ...read(env ? env.text : toolResultText(res.block.content)),
  };
}

function pollsOf(turns, fromTurn, cellId) {
  const polls = [];
  for (let ti = fromTurn; ti < turns.length; ti++) {
    for (const b of turns[ti]?.blocks || []) {
      if (!isWaitBlock(b) || String(b.input.cell_id) !== cellId) continue;
      const res = findResult(turns, b.id, ti);
      if (!res) continue;
      const env = parseCellEnvelope(res.block.content);
      polls.push({ res, env });
      if (env?.status !== "running") return polls;
    }
  }
  return polls;
}

// The result of a call is not in the call's own turn: the harness returns it as
// a tool_result in a LATER user turn, keyed by tool_use_id. Scanning forward
// from the call (rather than the whole trace) keeps a repeated positional id
// from matching an earlier turn's result, and the scan stops where the id is
// used again: a call whose result never came back would otherwise take the
// result of the later call that reused its id, hundreds of turns on.
//
// The two callers bound this differently ON PURPOSE, and the asymmetry is the
// opposite of an oversight:
//
//   readToolCallHead runs for EVERY unclaimed call on mount, so an unbounded
//   scan there is O(calls × turns) and a trace full of orphans goes quadratic.
//   It stops at HEAD_LOOKAHEAD_TURNS and reports `failed: null` — unknown —
//   rather than pretending the absence of a nearby result means success.
//
//   readToolCall runs ONCE, when a reader opens one row, so it searches the
//   whole remaining trace. Bounding it would make the payload claim "no result
//   paired to this call" for a result that is merely late, which is an assertion
//   of absence rather than a display cap — the exact failure the caps above are
//   written to avoid. Interleaved results are real: a long-running call's output
//   can land several turns after the calls that followed it. Measured worst case
//   (an 800-turn trace, orphan call in turn 0, every remaining turn walked) is
//   0.026ms, so there is nothing to buy by capping it.
function findResult(turns, id, fromTurn, untilTurn = Infinity) {
  if (!id) return null;
  const last = Math.min(turns.length - 1, untilTurn);
  for (let ti = fromTurn; ti <= last; ti++) {
    const blocks = turns[ti]?.blocks || [];
    for (let bi = 0; bi < blocks.length; bi++) {
      const b = blocks[bi];
      if (b?.type === "tool_result" && b.tool_use_id === id) return { block: b, turnIndex: ti, blockIndex: bi };
      if (ti > fromTurn && b?.type === "tool_use" && b.id === id && !b.virtual) return null;
    }
  }
  return null;
}

/**
 * Read one unclaimed tool call back out of the turns.
 *
 * Returns null when the coordinates don't resolve to a tool_use — the caller
 * then has nothing to expand, which is the truthful outcome rather than an
 * empty frame implying the content was found and was blank. `whole` reads the
 * call and its result uncapped, for a reader who opened this one call.
 */
export function readToolCall(turns, turnIndex, blockIndex, { whole = false } = {}) {
  const block = turns?.[turnIndex]?.blocks?.[blockIndex];
  if (!block || block.type !== "tool_use") return null;
  // A code-mode cell reads as what it DID: the inner command as the call, the
  // envelope-stripped stdout as the result, the envelope's own facts as a
  // note. The JS source stays one click away in callJson — recovered, never
  // erased.
  const cell = cellInnerReading(block, turns[turnIndex].blocks);
  const call = clipAt(cell ? cell.text : toolCallText(block.input), whole ? Infinity : CALL_CAP);
  const res = findResult(turns, block.id, turnIndex);
  // Envelope parsing is unconditional: a wait poll's result carries the same
  // host wrapper a cell's does, and a reader deserves the stripped output and
  // the status note either way.
  const env = res ? parseCellEnvelope(res.block.content) : null;
  const result = res ? clipAt(env ? env.text : toolResultText(res.block.content), whole ? Infinity : RESULT_CAP) : { text: "", clipped: 0 };
  return {
    name: cell ? `${block.name} · ${cell.tools.join(" · ")}` : String(block.name || "tool"),
    call: call.text,
    callClipped: call.clipped,
    // Kept unclipped: it is the escape hatch for a value the line reading
    // flattened, and it is only rendered on an explicit second click.
    callJson: block.input == null ? null : JSON.stringify(block.input, null, 2),
    result: result.text,
    resultClipped: result.clipped,
    resultNote: cellEnvelopeNote(env),
    failed: Boolean(res?.block?.isError) || (env?.exitCode != null && env.exitCode !== 0),
    // Where to land in the Context reading. The result's own position is a
    // separate fact from the call's — for a long-running call they are turns
    // apart, and "which turn returned this" is the question a reader asks when
    // an output looks stale.
    sources: [
      { turn: turnIndex, note: `block ${blockIndex} · call` },
      ...(res ? [{ turn: res.turnIndex, note: `block ${res.blockIndex} · result` }] : []),
    ],
  };
}
